import { parseDecimal } from '@catering/ui';
import { z } from 'zod';

import type { RegisterPaymentInput, WithholdingInput } from './api';

const positiveAmountString = z
  .string()
  .trim()
  .min(1, 'El monto es obligatorio')
  .refine((value) => parseDecimal(value) !== null, {
    message: 'Ingresá un monto válido, por ejemplo 1.500,50',
  })
  .refine((value) => (parseDecimal(value) ?? 1) > 0, {
    message: 'Ingresá un monto mayor a cero',
  });

const withholdingLineFormSchema = z.object({
  typeId: z.string().uuid({ message: 'Seleccioná un tipo' }),
  amountPesos: positiveAmountString,
  certificateNumber: z.string().trim().min(1, 'El número de certificado es obligatorio'),
  jurisdiction: z.string().trim(),
});

export type WithholdingLineFormValues = z.infer<typeof withholdingLineFormSchema>;

/**
 * Mirrors `apps/api` `registerPaymentSchema` (`payment.dto.ts`), with the
 * same pesos-typed-then-converted-to-cents convention `contract-price-form-schema.ts`
 * already established: every amount field stays a string bound to its
 * input, converted to integer cents only in `toRegisterPaymentInput` below
 * — never through a float division that could drift a cent.
 */
export const paymentFormSchema = z.object({
  customerId: z.string().uuid({ message: 'Seleccioná un cliente' }),
  paymentDate: z.string().trim().min(1, 'La fecha de pago es obligatoria'),
  referenceNumber: z.string().trim().min(1, 'Ingresá el número de operación, cheque o transferencia'),
  methodId: z.string().uuid({ message: 'Seleccioná un método' }),
  amountPesos: positiveAmountString,
  notes: z.string().trim().max(1000),
  withholdings: z.array(withholdingLineFormSchema),
});

export type PaymentFormValues = z.infer<typeof paymentFormSchema>;

export function defaultPaymentFormValues(paymentDate: string): PaymentFormValues {
  return {
    customerId: '',
    paymentDate,
    referenceNumber: '',
    methodId: '',
    amountPesos: '',
    notes: '',
    withholdings: [],
  };
}

export const DEFAULT_WITHHOLDING_LINE: WithholdingLineFormValues = {
  typeId: '',
  amountPesos: '',
  certificateNumber: '',
  jurisdiction: '',
};

/** Integer cents from a pesos string typed with up to two decimals, no float drift (`Math.round`, never plain division/multiplication chains). */
export function pesosToCents(pesos: string): number {
  return Math.round((parseDecimal(pesos) ?? Number.NaN) * 100);
}

export function toWithholdingInputs(lines: WithholdingLineFormValues[]): WithholdingInput[] {
  return lines.map((line) => ({
    typeId: line.typeId,
    amountCents: pesosToCents(line.amountPesos),
    certificateNumber: line.certificateNumber,
    ...(line.jurisdiction === '' ? {} : { jurisdiction: line.jurisdiction }),
  }));
}

/**
 * `idempotencyKey` is generated once per form instance by the caller
 * (`crypto.randomUUID()` at mount) and passed in here, never regenerated
 * on submit — a double submit (double click, retry after a dropped
 * response) must reuse the same key so the API's idempotent replay
 * (`payment-receipts/spec.md`) absorbs it instead of creating a second
 * receipt.
 */
export function toRegisterPaymentInput(
  values: PaymentFormValues,
  idempotencyKey: string,
): RegisterPaymentInput {
  return {
    customerId: values.customerId,
    paymentDate: values.paymentDate,
    referenceNumber: values.referenceNumber,
    methodId: values.methodId,
    amountReceivedCents: pesosToCents(values.amountPesos),
    ...(values.notes === '' ? {} : { notes: values.notes }),
    idempotencyKey,
    withholdings: toWithholdingInputs(values.withholdings),
  };
}

/** Total applicable amount (recibido + retenciones), for the live total shown in the form. */
export function totalApplicableCents(
  values: Pick<PaymentFormValues, 'amountPesos' | 'withholdings'>,
): number {
  const received = Number.isFinite(pesosToCents(values.amountPesos)) ? pesosToCents(values.amountPesos) : 0;
  const withholdings = values.withholdings.reduce((sum, line) => {
    const cents = pesosToCents(line.amountPesos);
    return sum + (Number.isFinite(cents) ? cents : 0);
  }, 0);

  return received + withholdings;
}
